import React, { useState, useEffect } from 'react';
import { User } from './types';
import { getUsersByCustomerId, createUser, updateUser, deleteUser } from '../../api';

interface UserManagementProps {}

const UserManagement: React.FC<UserManagementProps> = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'Member'
  });
  const [userFormErrors, setUserFormErrors] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; userId: number | null; userName: string }>({ isOpen: false, userId: null, userName: '' });

  // Fetch current user from localStorage
  useEffect(() => {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      try {
        const userData = JSON.parse(userJson);
        setCurrentUser(userData);
      } catch (err) {
        console.error('Failed to parse user data:', err);
      }
    }
  }, []);

  // Fetch users when component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      if (!currentUser?.customerId) return;
      
      try {
        setLoadingUsers(true);
        const response = await getUsersByCustomerId(currentUser.customerId);
        if (response && response.success && Array.isArray(response.users)) {
          setUsers(response.users);
        } else {
          console.error('Invalid response format:', response);
          setErrorMessage('Failed to load users. Please try again.');
          setUsers([]); // Ensure users is always an array
        }
      } catch (err) {
        console.error('Failed to load users:', err);
        setErrorMessage('Failed to load users. Please try again.');
        setUsers([]); // Ensure users is always an array
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [currentUser]);

  const handleCreateUser = async () => {
    // Validate form data
    const errors: Record<string, string> = {};
    if (!userFormData.email) errors.email = 'Email is required';
    if (!userFormData.password) errors.password = 'Password is required';
    if (Object.keys(errors).length > 0) {
      setUserFormErrors(errors);
      return;
    }
    
    try {
      if (!currentUser?.customerId) {
        throw new Error('Customer ID not found');
      }
      
      // Create the user
      const response = await createUser({
        email: userFormData.email,
        password: userFormData.password,
        firstName: userFormData.firstName || undefined,
        lastName: userFormData.lastName || undefined,
        customerId: currentUser.customerId,
        role: userFormData.role
      });
      
      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to create user');
      }
      
      // Refresh the users list
      const usersResponse = await getUsersByCustomerId(currentUser.customerId);
      if (usersResponse && usersResponse.success && Array.isArray(usersResponse.users)) {
        setUsers(usersResponse.users);
      }
      
      // Reset the form
      setUserFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'Member'
      });
      setUserFormErrors({});
      setIsCreatingUser(false);
      setSuccessMessage('User created successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Failed to create user:', err);
      setErrorMessage(err.message || 'Failed to create user. Please try again.');
    }
  };
  
  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    // Validate form data
    const errors: Record<string, string> = {};
    if (!userFormData.email) errors.email = 'Email is required';
    if (Object.keys(errors).length > 0) {
      setUserFormErrors(errors);
      return;
    }
    
    try {
      // Update the user
      const response = await updateUser(editingUser.userId, {
        email: userFormData.email,
        password: userFormData.password || undefined,
        firstName: userFormData.firstName || undefined,
        lastName: userFormData.lastName || undefined,
        role: userFormData.role
      });
      
      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to update user');
      }
      
      // Refresh the users list
      if (currentUser?.customerId) {
        const usersResponse = await getUsersByCustomerId(currentUser.customerId);
        if (usersResponse && usersResponse.success && Array.isArray(usersResponse.users)) {
          setUsers(usersResponse.users);
        }
      }
      
      // Reset the form
      setUserFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'Member'
      });
      setUserFormErrors({});
      setEditingUser(null);
      setSuccessMessage('User updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      // If the current user was updated, refresh the current user data in localStorage
      if (editingUser.userId === currentUser?.userId) {
        const updatedUserData = {
          ...currentUser,
          email: userFormData.email,
          firstName: userFormData.firstName || null,
          lastName: userFormData.lastName || null,
          role: userFormData.role
        };
        localStorage.setItem('user', JSON.stringify(updatedUserData));
      }
    } catch (err: any) {
      console.error('Failed to update user:', err);
      setErrorMessage(err.message || 'Failed to update user. Please try again.');
    }
  };
  
  const handleDeleteUser = async (userId: number) => {
    try {
      // Delete the user
      const response = await deleteUser(userId);
      
      // Close the confirmation modal
      setDeleteConfirmModal({ isOpen: false, userId: null, userName: '' });
      
      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to delete user');
      }
      
      // Refresh the users list
      if (currentUser?.customerId) {
        const usersResponse = await getUsersByCustomerId(currentUser.customerId);
        if (usersResponse && usersResponse.success && Array.isArray(usersResponse.users)) {
          setUsers(usersResponse.users);
        }
      }
      
      setSuccessMessage('User deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setErrorMessage(err.message || 'Failed to delete user. Please try again.');
      // Close the confirmation modal on error too
      setDeleteConfirmModal({ isOpen: false, userId: null, userName: '' });
    }
  };
  
  const handleDeleteUserClick = (user: User) => {
    const userName = user.firstName && user.lastName 
      ? `${user.firstName} ${user.lastName}` 
      : user.email;
    setDeleteConfirmModal({ isOpen: true, userId: user.userId, userName });
  };
  
  const handleConfirmDelete = () => {
    if (deleteConfirmModal.userId) {
      handleDeleteUser(deleteConfirmModal.userId);
    }
  };
  
  const handleCancelDelete = () => {
    setDeleteConfirmModal({ isOpen: false, userId: null, userName: '' });
  };
  
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserFormData({
      email: user.email,
      password: '', // Don't populate password for security reasons
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role
    });
    setUserFormErrors({});
    setIsCreatingUser(false);
  };
  
  const handleUserFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (userFormErrors[name]) {
      setUserFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleCancelForm = () => {
    setEditingUser(null);
    setIsCreatingUser(false);
    setUserFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'Member'
    });
    setUserFormErrors({});
  };

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'Admin';

  if (loadingUsers) {
    return <div>Loading users...</div>;
  }

  return (
    <div>
      <h3>User Management</h3>
      <p>Manage users for your organization.</p>
      
      {errorMessage && (
        <div style={{ 
          backgroundColor: '#ffebee', 
          color: '#c62828', 
          padding: '12px 16px', 
          borderRadius: 4,
          marginBottom: 16 
        }}>
          {errorMessage}
          <button 
            onClick={() => setErrorMessage(null)} 
            style={{ 
              background: 'none',
              border: 'none',
              color: '#c62828',
              cursor: 'pointer',
              float: 'right',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {successMessage && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          padding: '12px 16px', 
          borderRadius: 4,
          marginBottom: 16 
        }}>
          {successMessage}
          <button 
            onClick={() => setSuccessMessage(null)} 
            style={{ 
              background: 'none',
              border: 'none',
              color: '#155724',
              cursor: 'pointer',
              float: 'right',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* User Form (Create/Edit) */}
      {(isCreatingUser || editingUser) && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '12px 48px 24px 24px', 
          borderRadius: 8,
          marginBottom: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <h4>{editingUser ? 'Edit User' : 'Create New User'}</h4>
          
          <div style={{ marginBottom: 16 }}>
            <label 
              htmlFor="email"
              style={{ 
                display: 'block', 
                marginBottom: 8, 
                fontWeight: 500 
              }}
            >
              Email<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={userFormData.email}
              onChange={handleUserFormChange}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                borderRadius: 4, 
                border: userFormErrors.email ? '1px solid red' : '1px solid #ddd',
                fontSize: 16
              }}
            />
            {userFormErrors.email && (
              <div style={{ color: 'red', fontSize: 14, marginTop: 4 }}>
                {userFormErrors.email}
              </div>
            )}
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label 
              htmlFor="password"
              style={{ 
                display: 'block', 
                marginBottom: 8, 
                fontWeight: 500 
              }}
            >
              Password{!editingUser && <span style={{ color: 'red' }}>*</span>}
              {editingUser && <span style={{ color: '#666', fontSize: 14, fontWeight: 'normal', marginLeft: 8 }}>
                (Leave blank to keep current password)
              </span>}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={userFormData.password}
              onChange={handleUserFormChange}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                borderRadius: 4, 
                border: userFormErrors.password ? '1px solid red' : '1px solid #ddd',
                fontSize: 16
              }}
            />
            {userFormErrors.password && (
              <div style={{ color: 'red', fontSize: 14, marginTop: 4 }}>
                {userFormErrors.password}
              </div>
            )}
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label 
              htmlFor="firstName"
              style={{ 
                display: 'block', 
                marginBottom: 8, 
                fontWeight: 500 
              }}
            >
              First Name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={userFormData.firstName}
              onChange={handleUserFormChange}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                borderRadius: 4, 
                border: '1px solid #ddd',
                fontSize: 16
              }}
            />
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label 
              htmlFor="lastName"
              style={{ 
                display: 'block', 
                marginBottom: 8, 
                fontWeight: 500 
              }}
            >
              Last Name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={userFormData.lastName}
              onChange={handleUserFormChange}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                borderRadius: 4, 
                border: '1px solid #ddd',
                fontSize: 16
              }}
            />
          </div>
          
          {/* Role selection (only for admins) */}
          {isAdmin && (
            <div style={{ marginBottom: 16 }}>
              <label 
                htmlFor="role"
                style={{ 
                  display: 'block', 
                  marginBottom: 8, 
                  fontWeight: 500 
                }}
              >
                Role
              </label>
              <select
                id="role"
                name="role"
                value={userFormData.role}
                onChange={handleUserFormChange}
                style={{ 
                  width: '100%', 
                  padding: '8px 12px', 
                  borderRadius: 4, 
                  border: '1px solid #ddd',
                  fontSize: 16
                }}
              >
                <option value="Member">Member</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
            <button
              onClick={editingUser ? handleUpdateUser : handleCreateUser}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              {editingUser ? 'Update User' : 'Create User'}
            </button>
            <button
              onClick={handleCancelForm}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f0f0f0',
                color: 'black',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Create User Button (only for admins) */}
      {!isCreatingUser && !editingUser && isAdmin && (
        <button
          onClick={() => setIsCreatingUser(true)}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            marginBottom: 24
          }}
        >
          Create New User
        </button>
      )}
      
      {/* Users List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {users.length === 0 ? (
          <p>No users found.</p>
        ) : (
          users.map(user => (
            <div 
              key={user.userId} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: 16, 
                backgroundColor: 'white', 
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: 0, marginBottom: 4 }}>
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user.email}
                  {user.userId === currentUser?.userId && (
                    <span style={{ 
                      fontSize: 12, 
                      backgroundColor: '#e0e0e0', 
                      padding: '2px 6px', 
                      borderRadius: 12,
                      marginLeft: 8
                    }}>
                      You
                    </span>
                  )}
                </h4>
                <p style={{ margin: 0, color: '#666', fontSize: 14 }}>{user.email}</p>
                <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
                  Role: <span style={{ 
                    fontWeight: 'bold', 
                    color: user.role === 'Admin' ? '#1976d2' : '#666'
                  }}>
                    {user.role}
                  </span>
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Edit button - admins can edit anyone, members can only edit themselves */}
                {(isAdmin || user.userId === currentUser?.userId) && (
                  <button
                    onClick={() => handleEditUser(user)}
                    style={{ 
                      padding: '6px 12px', 
                      backgroundColor: '#f0f0f0',
                      color: 'black',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                )}
                
                {/* Delete button - only admins can delete, and they can't delete themselves */}
                {isAdmin && user.userId !== currentUser?.userId && (
                  <button
                    onClick={() => handleDeleteUserClick(user)}
                    style={{ 
                      padding: '6px 12px', 
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: '1.5' }}>
              Are you sure you want to delete <strong>{deleteConfirmModal.userName}</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelDelete}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
